import { listMyDevices, registerMyDevice } from '../../../services/appsync-client'
import { getDeviceId, primeDeviceId } from '../../../utils/device-id'
import { collectRegistrationPayload } from '../../devices/hooks/use-device-registry'
import { summarizeDeviceApproval } from '../../devices/utils/device-approval-status'
import { withTimeout } from './setup-probes'

const UNREGISTERED_RETRY_MS = 3_000
const DEVICE_PROBE_TIMEOUT_MS = 8_000

export type ThisDeviceApproval = 'trusted' | 'pending' | 'unknown'

async function probeUncapped(cancel: { cancelled: boolean }): Promise<ThisDeviceApproval> {
  await primeDeviceId()
  let deviceId = getDeviceId()
  let devices = (await listMyDevices()).devices
  if (!devices.some((d) => d.deviceId === deviceId)) {
    // First sign-in: this install's registration may not have landed yet.
    await new Promise((resolve) => setTimeout(resolve, UNREGISTERED_RETRY_MS))
    if (cancel.cancelled) return 'unknown'
    devices = (await listMyDevices()).devices
    if (!devices.some((d) => d.deviceId === deviceId)) return 'unknown'
  }
  let summary = summarizeDeviceApproval(devices, deviceId)
  if (summary.kind === 'this-pending' && summary.approvers.length === 0) {
    // Nothing is left that could approve this install: re-registering trusts it
    // server-side (same rule as an account's first device).
    if (cancel.cancelled) return 'unknown'
    await registerMyDevice(await collectRegistrationPayload())
    deviceId = getDeviceId()
    summary = summarizeDeviceApproval((await listMyDevices()).devices, deviceId)
  }
  return summary.kind === 'this-pending' ? 'pending' : 'trusted'
}

/** Whether this install still waits for approval (#499). Capped; never blocks setup. */
export function probeThisDeviceApproval(): Promise<ThisDeviceApproval> {
  const cancel = { cancelled: false }
  return withTimeout<ThisDeviceApproval>(
    probeUncapped(cancel).catch(() => 'unknown' as const),
    'unknown',
    DEVICE_PROBE_TIMEOUT_MS,
  ).finally(() => {
    cancel.cancelled = true
  })
}
